/* global $:false */

import { DEV_MODE } from './cob.devmode.js';
import director from 'director/build/director.min.js';

// This helper provides support for routing between modules.
var Routing = function(core, opts) {

    var _router = director.Router().configure({
        // If set to false, then trailing slashes (or other delimiters) are allowed in routes
        strict: false,
        notfound: function() {
           window.console.log("No module found for route \"" + _router.getRoute() + "\". " +
              "Redirecting to default module \"" + opts.getDefaultModuleUri() +"\".");
           // we use a small timeout because if we have a cycle, it freezes the browser
           setTimeout(() => { _router.setRoute("/")}, 50);
        }
    });

    var forwardToDefaultAppModuleUri= function(){
        _router.setRoute(opts.getDefaultModuleUri());
    };

    _router.on("/", forwardToDefaultAppModuleUri);

    var _alreadyShowingLoading = false,
        _loadingKey,
    //set this to true to change route without navigating
        _noNav = false;

    var _addStateId = function(uri) {
        var _uri = uri || _getCurrentRoute(),
            key = "_=";

        // do nothing if we already have a stateId
        if (_uri.indexOf('_=') !== -1) return _uri;

        if (_uri.indexOf("?") !== -1) {
            key = "&" + key;
        } else {
            key = "?" + key;
        }

        return _uri + key + encodeURIComponent(
            $.base64.encode([_uri , new Date().valueOf()].join("-"))
        );
    }

    var _getCurrentRoute = function() {
        return "/" + _router.getRoute().join("/");
    }

    var _onAnchorClick = function(e) {
        // remove the # from the href
        /* eslint-disable-next-line no-invalid-this */
        var element = $(this),
            uri = element.attr('href').substring(1),
            target = element.attr('target');

        if (!target && !(e.metaKey || e.ctrlKey || e.shiftKey)) {
            e.preventDefault();

            _router.setRoute(_addStateId(uri));
        }
    }

    /**
     * When CAS is enabled and a user is trying to access the app login form,
     * we redirect the user to the app homepage after logging in in CAS
     * instead of redirecting it to the login form.
     *
     * If the app URL has fragments, we have to remove them from the URL and create a fragment query parameter
     * in order to be able to autenticate via CAS.
     * {@see com.cultofbits.genesis.security.http.filter.SecurityFilter}
     */
    var _goToCasLoginPage = function(casBaseUrl) {
        const currentURL = new URL(window.location)
        const fragmentWithoutState = currentURL.hash.replace(/(\?|&)_=.*/gi, '')

        if(fragmentWithoutState.indexOf("login") === -1){
           currentURL.searchParams.set('fragment', fragmentWithoutState)
        }
        currentURL.hash = '';

        // we need this to be equal to what is done in SecurityFilter.getCasService
        if(currentURL.pathname === '/') currentURL.pathname = '/index.html';

        var encodedAppUrl = encodeURIComponent(currentURL.href);
        window.location = casBaseUrl + "/login?service=" + encodedAppUrl;
    };

    var _safeShowLoading = function(msg) {
        if (!_alreadyShowingLoading) {
            _alreadyShowingLoading = true;
            core.showLoading(_loadingKey, msg);
        }
    };

    return {
        start: function(routingBaseSelector) {
            // we normally listen only for clicks inside the app,
            // but we may need to listen to clicks somewhere else (gdpr)
            $(routingBaseSelector || '#'+core.getAppInstanceId()).on('click', 'a[href^="#/"]', _onAnchorClick);

            _loadingKey = core.getLoadingKey();

            _router.init();

            // if there is no route
            if (window.location.href.indexOf('#') === -1) {
                //we go directly to the default module, so that we don't create useless history entries
                window.location.replace(
                    String(window.location).split('#')[0] + '#/' + opts.getDefaultModuleUri()
                );
            }
        },
        register: function(uri, moduleName) {
            DEV_MODE && window.console.log("Registering uri routing ", uri, "for module", moduleName);

            _router.on(uri, function() {
                if (_noNav) {
                    _noNav = false;
                    return;
                }

                var queryString = _parseQueryString(window.location),
                    args,
                    stateId;

                _safeShowLoading("navigating by external change to " + window.location.hash +
                                 " matching uri " + uri);

                if (queryString && queryString._) {
                    stateId = queryString._;

                } else {
                    // add a stateId to the current hash, without modifying history
                    window.location.replace(String(window.location).split('#')[0] + '#' + _addStateId());
                    stateId = _parseQueryString(window.location)._;
                    // director will fire again, becase we replaced the current location
                    // and we don't want to do anything
                    _noNav = true;
                }

                /* eslint-disable prefer-rest-params */
                if (arguments) {

                    //convert the arguments object into an array
                    args = Array.prototype.slice.call(arguments);

                    // Decode the URI components
                    args = args.map(decodeURIComponent);
                }

                DEV_MODE && window.console.log("Starting module name", moduleName, "with arguments", args, "and state", stateId);
                opts.goTo(moduleName, args, stateId, window.location.hash);

                core.hideLoading(_loadingKey, "Navigated!");
                _alreadyShowingLoading = false;
            });
        },
        navigateToAppHomepage:function() {
            //If the logged in user is now impersonating the selected user
            //we redirect him/her to the application "homepage".
            window.location = window.location.toString().split("#")[0];
        },
        navigateTo: function (uri) {
            _safeShowLoading("navigating to " + uri);
            _router.setRoute(_addStateId(uri));
        },
        navigateToCurrentRoute: function() {
            var uri = _getCurrentRoute();
            _safeShowLoading("navigating to " + uri);

            if (uri !== '/' && uri !== '/login') {
                _router.setRoute(uri);

            } else {
                forwardToDefaultAppModuleUri();
            }
        },
        navigateToCasLogin: _goToCasLoginPage,
        navigateToCasLogout: function(casBaseUrl){
            window.location = casBaseUrl + "/logout";
        },
        navigateBack: function(howMuch) {
            var _howMuch = howMuch || 1;

            // if we have no history, we can't go anywhere (the url was entered directly)
            if (core.moduleStackLength() > 1) {
                _safeShowLoading("navigating back in history by " + _howMuch);
                window.history.go(-1 * _howMuch);

            } else {
                // if no history then navigate to root application path
                DEV_MODE && window.console.log("No back history. Redirecting to /");
                window.location = "/";
            }
        },
        changeRouteWithoutNavigation: function(uri) {
            _noNav = true;
            _router.setRoute(_addStateId(uri));
        }
    };
};

var _parseQueryString = function(location) {
   var queryString = location.hash.split('?')[1];

   if (queryString) {
      var qs = {};

      queryString.split('&').forEach(function(item) {
         var data = item.split("=");

         qs[data[0]] = data[1];
      });

      return qs;
   }

   return undefined;
}

export {Routing as default, _parseQueryString }
